import React from 'react';
import { Card, CardContent, Stack, Box, Typography, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ExtraActionsMenu } from '../../components';
import StatusIndicator from './StatusIndicator';
import OperationLocations from './OperationLocations';
import OperationDateInfo from './OperationDateInfo';
import IncidentCommanderPill from './IncidentCommanderPill';
import { Operation } from '../../../../networkRequest/operationsService';
import { PAGE_PATHS } from '../../../../constants';

interface OperationCardProps {
  operation: Operation;
  onDuplicate: (operation: Operation) => void;
  onArchive: (operation: Operation) => void;
  duplicating?: boolean;
}

const OperationCard: React.FC<OperationCardProps> = ({
  operation,
  onDuplicate,
  onArchive,
  duplicating = false
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`${PAGE_PATHS.operations}/${operation.id}`);
  };

  const handleDuplicate = () => {
    onDuplicate(operation);
  };

  const handleArchive = () => {
    onArchive(operation);
  };

  // Filter for Incident Commanders and Deputy Incident Commanders
  const incidentCommanders =
    operation.operation_agency_officials?.filter(
      (o) =>
        o.active &&
        (o.assignment === 'Incident Commander' || o.assignment === 'Deputy Incident Commander')
    ) || [];

  return (
    <Card
      variant="outlined"
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
      onClick={handleCardClick}>
      <CardContent sx={{ padding: 2, '&:last-child': { paddingBottom: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{
            alignItems: { xs: 'flex-start' }
          }}>
          <Box sx={{ flex: 1 }}>
            <Typography component="h1" variant="h4">{operation.title}</Typography>
            <OperationLocations operation={operation} />

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                maxWidth: 745
              }}>
              {operation.description}
            </Typography>
          </Box>

          <Box sx={{ width: '17px' }} />

          <Box>
            <OperationDateInfo operation={operation} />

            {/* Incident Commanders Section */}
            {incidentCommanders.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  component="h2"
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    letterSpacing: 0.5,
                    mb: 1
                  }}>
                  Incident Commanders
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {incidentCommanders.map((o) => (
                    <IncidentCommanderPill
                      key={o.id}
                      photoUrl={o.agency_official?.img_url}
                      assignment={o.assignment}
                      name={
                        o.agency_official
                          ? `${o.agency_official.first_name} ${o.agency_official.last_name}`
                          : ''
                      }
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "flex-start",
              pt: '6px',
              minWidth: 90
            }}>
            <StatusIndicator status={operation.status} />
            <Typography component="p" variant="subtitle2" sx={{ fontWeight: 600 }}>
              {operation.status.charAt(0).toUpperCase() + operation.status.slice(1)}
            </Typography>
          </Stack>

          <Box sx={{ paddingTop: '4px' }}>
            <ExtraActionsMenu
              onDuplicate={handleDuplicate}
              onArchive={handleArchive}
              duplicating={duplicating}
              archiveDisabled={operation.status === 'archived'}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default OperationCard;
